// /settings/telegram — connect personal Telegram accounts to the portal's own
// client: app keys (api_id / api_hash from my.telegram.org), then per account
// phone → code from Telegram → cloud password if set. Owners decide who sees
// the account's chats.
import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { KeyRound, Loader2, LogOut, Plus, RefreshCw, Send } from "lucide-react";
import { accountLabel, errText } from "../components/TgChat";

function AddAccount({ onDone }) {
  const [step, setStep] = useState("phone"); // phone | code | password
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [loginId, setLoginId] = useState("");
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/tg/accounts/login/start", { phone });
      setLoginId(r.data.login_id);
      setStep("code");
      toast.success("Code sent — it arrives in the Telegram app on that account");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const finish = async () => {
    setBusy(true);
    try {
      const r = await api.post("/tg/accounts/login/finish", { login_id: loginId, code, password: step === "password" ? password : undefined });
      if (r.data.status === "need_password") { setStep("password"); return; }
      toast.success(`Connected: ${r.data.username ? "@" + r.data.username : r.data.name}`);
      setStep("phone"); setPhone(""); setCode(""); setPassword(""); setLoginId("");
      onDone();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-zinc-800 p-3 space-y-2" data-testid="tg-add-account">
      <p className="text-sm text-zinc-200 flex items-center gap-2"><Plus className="h-4 w-4" />Connect an account</p>
      {step === "phone" && (
        <div className="flex flex-wrap items-center gap-2">
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+380…"
                 className="bg-zinc-800 border-zinc-700 text-zinc-50 h-9 w-56" />
          <Button onClick={start} disabled={busy || phone.replace(/\D/g, "").length < 8} className="h-9">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Send code
          </Button>
        </div>
      )}
      {step === "code" && (
        <div className="flex flex-wrap items-center gap-2">
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code from Telegram" autoComplete="one-time-code"
                 className="bg-zinc-800 border-zinc-700 text-zinc-50 h-9 w-56" />
          <Button onClick={finish} disabled={busy || !code.trim()} className="h-9">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Sign in
          </Button>
          <button className="text-xs text-zinc-500 hover:text-zinc-300" onClick={() => setStep("phone")}>change number</button>
        </div>
      )}
      {step === "password" && (
        <div className="flex flex-wrap items-center gap-2">
          <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder="Cloud password (2FA)"
                 autoComplete="current-password" className="bg-zinc-800 border-zinc-700 text-zinc-50 h-9 w-56" />
          <Button onClick={finish} disabled={busy || !password} className="h-9">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Sign in
          </Button>
        </div>
      )}
      <p className="text-[11px] text-zinc-500">
        The portal becomes one more device in Telegram → Settings → Devices; the phone keeps working. End that session there
        to cut access at any time.
      </p>
    </div>
  );
}

export default function TelegramSettingsPage() {
  const [st, setSt] = useState(null);
  const [apiId, setApiId] = useState("");
  const [apiHash, setApiHash] = useState("");
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/tg/status").then((r) => { setSt(r.data); if (r.data.api_id) setApiId(String(r.data.api_id)); })
    .catch((e) => toast.error(errText(e)));
  useEffect(() => {
    load();
    api.get("/employees/directory").then((r) => setStaff(Array.isArray(r.data) ? r.data : [])).catch(() => setStaff([]));
  }, []);

  const saveConfig = async () => {
    setBusy("cfg");
    try {
      await api.put("/tg/config", { api_id: apiId, api_hash: apiHash });
      setApiHash("");
      toast.success("Saved");
      load();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  const act = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  const setOwners = (a, ids) => act(`own-${a.id}`, () => api.put(`/tg/accounts/${a.id}/owners`, { staff_ids: ids }), "Saved");

  if (!st) return <div className="flex justify-center h-64 items-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-500" /></div>;

  return (
    <div data-testid="telegram-settings-page" className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope'] flex items-center gap-3">
          <Send className="h-6 w-6 text-sky-400" />Telegram — own connection
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Personal Telegram accounts connected straight to the portal. Sessions are stored encrypted on our server only.
        </p>
      </div>

      {!st.library && (
        <Card className="bg-zinc-900 border-red-500/30"><CardContent className="py-3 text-sm text-red-400">
          The Telegram library is not installed on the server — redeploy the backend.
        </CardContent></Card>
      )}

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2"><CardTitle className="text-base text-zinc-100 flex items-center gap-2"><KeyRound className="h-4 w-4" />App keys</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-zinc-500">
            From my.telegram.org → API development tools. One pair serves every account.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <Label className="text-xs text-zinc-400">api_id</Label>
              <Input value={apiId} onChange={(e) => setApiId(e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-50 h-9 w-36" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-zinc-400">api_hash</Label>
              <Input value={apiHash} onChange={(e) => setApiHash(e.target.value)} type="password"
                     placeholder={st.configured ? "saved — paste to replace" : "32 characters"}
                     className="bg-zinc-800 border-zinc-700 text-zinc-50 h-9 w-80" />
            </div>
            <Button onClick={saveConfig} disabled={busy === "cfg" || !apiId || apiHash.length !== 32} className="h-9">Save</Button>
            <span className={cn("text-xs ml-1", st.configured ? "text-emerald-400" : "text-zinc-500")}>
              {st.configured ? "● keys set" : "○ not set"}
            </span>
          </div>
          {!st.own_session_key && (
            <p className="text-[11px] text-amber-400">
              Sessions are encrypted with a key derived from the portal secret. Set TG_SESSION_KEY on the server to make it independent.
            </p>
          )}
        </CardContent>
      </Card>

      {st.configured && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2"><CardTitle className="text-base text-zinc-100">Accounts</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {st.accounts.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-zinc-800 px-3 py-2">
                <span className="text-sm text-zinc-100 min-w-[180px]">
                  {accountLabel(a)} {a.phone && <span className="text-zinc-500 text-xs">+{a.phone}</span>}
                </span>
                <span className={cn("text-xs", a.connected ? "text-emerald-400" : "text-amber-400")}>
                  {a.connected ? "● online" : `○ ${a.status}`}
                </span>
                {a.last_error && <span className="text-xs text-amber-400">{a.last_error}</span>}
                {a.staff_ids.map((sid, i) => (
                  <span key={sid} className="text-xs px-2 py-0.5 rounded-md border border-sky-500/40 text-sky-300 bg-sky-500/10 flex items-center gap-1">
                    {a.staff_names[i]}
                    <button className="hover:text-red-400" onClick={() => setOwners(a, a.staff_ids.filter((x) => x !== sid))}>×</button>
                  </span>
                ))}
                <select value="" onChange={(e) => e.target.value && setOwners(a, [...a.staff_ids, e.target.value])}
                        className="bg-zinc-800 border border-zinc-700 text-zinc-300 h-7 rounded-md px-2 text-xs">
                  <option value="">+ who sees it</option>
                  {staff.filter((s) => !a.staff_ids.includes(s.id)).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <div className="ml-auto flex gap-1">
                  {!a.connected && a.status !== "logged_out" && (
                    <Button size="sm" variant="ghost" className="h-7 text-zinc-400" disabled={!!busy}
                            onClick={() => act(`rc-${a.id}`, () => api.post(`/tg/accounts/${a.id}/reconnect`), "Reconnected")}>
                      <RefreshCw className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {a.status !== "logged_out" && (
                    <Button size="sm" variant="ghost" className="h-7 text-red-400" disabled={!!busy}
                            onClick={() => window.confirm(`Log ${accountLabel(a)} out of the portal? Its device disappears from Telegram; chats already saved stay.`)
                              && act(`lo-${a.id}`, () => api.post(`/tg/accounts/${a.id}/logout`), "Logged out")}>
                      <LogOut className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            <AddAccount onDone={load} />
            <p className="text-[11px] text-zinc-500">
              Writing first to a new person (from a client card) is capped at 20 per hour per account — Telegram limits accounts that cold-message strangers.
            </p>
          </CardContent>
        </Card>
      )}

      {st.accounts.length > 0 && <ApiTokens />}
    </div>
  );
}

// Personal send-only tokens for the owner's own tools (e.g. a local task board).
function ApiTokens() {
  const [tokens, setTokens] = useState([]);
  const [label, setLabel] = useState("");
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/tg/api-tokens").then((r) => setTokens(r.data.tokens)).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post("/tg/api-tokens", { label });
      setFresh(r.data.token); setLabel(""); load();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const revoke = async (t) => {
    if (!window.confirm(`Revoke "${t.label}"? Anything using it stops sending at once.`)) return;
    try { await api.delete(`/tg/api-tokens/${t.id}`); load(); toast.success("Revoked"); } catch (e) { toast.error(errText(e)); }
  };
  const fmt = (d) => (d ? new Date(d).toLocaleString() : "never");
  const setRead = async (t, on) => {
    if (on && !window.confirm(`Let "${t.label}" read all your Telegram chats? Every read is logged; you can switch it off here.`)) return;
    try { await api.put(`/tg/api-tokens/${t.id}`, { can_read: on }); load(); } catch (e) { toast.error(errText(e)); }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader className="pb-2"><CardTitle className="text-base text-zinc-100 flex items-center gap-2"><Send className="h-4 w-4" />API tokens</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <p className="text-[11px] text-zinc-500">
          A token sends messages from your accounts only. Reading chats is off unless you tick it for that token. Every send and read is logged. Revoke it the moment you no longer trust where it lives.
        </p>
        {tokens.map((t) => (
          <div key={t.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-zinc-800 px-3 py-2 text-sm">
            <span className="text-zinc-100 min-w-[140px]">{t.label}</span>
            <span className="font-mono text-xs text-zinc-500">{t.prefix}…</span>
            <span className="text-xs text-zinc-500">created {fmt(t.created_at)} · last used {fmt(t.last_used_at)}</span>
            <label className={cn("text-xs flex items-center gap-1 cursor-pointer", t.can_read ? "text-amber-300" : "text-zinc-400")}>
              <input type="checkbox" checked={t.can_read} onChange={(e) => setRead(t, e.target.checked)} />
              can read chats
            </label>
            <Button size="sm" variant="ghost" className="h-7 ml-auto text-red-400" onClick={() => revoke(t)}>Revoke</Button>
          </div>
        ))}
        {fresh && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 space-y-1">
            <p className="text-xs text-amber-300">Copy it now — it is shown only once.</p>
            <div className="flex gap-2">
              <Input readOnly value={fresh} className="bg-zinc-800 border-zinc-700 text-zinc-100 font-mono text-xs h-9" onFocus={(e) => e.target.select()} />
              <Button className="h-9" onClick={() => { navigator.clipboard?.writeText(fresh); toast.success("Copied"); }}>Copy</Button>
              <Button variant="ghost" className="h-9 text-zinc-400" onClick={() => setFresh(null)}>Done</Button>
            </div>
          </div>
        )}
        <div className="flex gap-2">
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="What it is for, e.g. Pult on my Mac"
                 className="bg-zinc-800 border-zinc-700 text-zinc-100 h-9" />
          <Button onClick={create} disabled={busy} className="h-9"><Plus className="h-4 w-4 mr-1" />New token</Button>
        </div>
      </CardContent>
    </Card>
  );
}
